import { useDemoMode, useDemoModeFromAuth } from './useDemoMode';
import type { ApiClient } from '@/lib/api';

export interface DemoFilter {
  /**
   * Value to pass as `?includeDemo=` query param to backend endpoints that
   * support it (currently: `/api/v1/records`, dashboard). The name is
   * camelCase on purpose — listRecordsQuerySchema declares `includeDemo`, and
   * a snake_case spelling is silently dropped by the Zod validator, falling
   * back to `default('yes')`.
   *
   * - `'yes'`: include demo rows (default — used when the user has NO real
   *   records and we want to show the seeded demo).
   * - `'no'`: exclude demo rows (used after the first real record arrives,
   *   per the auto-hide behavior).
   */
  includeDemo: 'yes' | 'no';
}

export function useDemoFilter(): DemoFilter {
  const { isDemoActive } = useDemoMode();
  return {
    includeDemo: isDemoActive ? 'yes' : 'no',
  };
}

/**
 * Variant of useDemoFilter() for components that render in both the Clerk app
 * and the embedded/Bitrix24 iframe and already have apiClient/orgId from the
 * mode-agnostic `useAuth()` (UnifiedAuthContext). See useDemoModeFromAuth for
 * why the plain useDemoFilter()/useDemoMode() can't be used there.
 */
export function useDemoFilterFromAuth(
  apiClient: ApiClient,
  orgId: string | null
): DemoFilter & { isLoading: boolean } {
  const { isDemoActive, isLoading } = useDemoModeFromAuth(apiClient, orgId);
  return {
    includeDemo: isDemoActive ? 'yes' : 'no',
    // Until the status is known `includeDemo` reads 'no'; a request sent now
    // would be thrown away (and, for a demo-only org, show an empty screen).
    isLoading,
  };
}
